import { formatDistance } from 'date-fns';
import { CheckCircle2, CircleDashed } from 'lucide-react';

import { useCurrentDate } from '@/hooks/current-date-provider';
import { ShipmentMilestone } from '@/lib/models/shipment/types';
import { formatDateTimeForUser, tryParseDate } from '@/utils/format/date-time';
import { tv } from '@/utils/styles';

export interface ShipmentMilestoneEventProps {
  milestone: ShipmentMilestone;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  isLast?: boolean;
}

const ShipmentMilestoneEvent = ({ milestone, isFirstInGroup, isLastInGroup, isLast }: ShipmentMilestoneEventProps) => {
  const currentDate = useCurrentDate();
  const { completed, label, locationName, locationCode, date: milestoneDate } = milestone;

  // Leftover to try and handle old cargoes dates that might be in the data
  const parsedMilestoneDate = tryParseDate(milestoneDate);

  const {
    base,
    icon,
    content,
    marker,
    locationWrap,
    locationLabel,
    locationBadge,
    locationBadgeBlock,
    eventType,
    eventDate,
    progressBar,
  } = milestoneStyles({
    isComplete: completed,
    isLastInGroup,
    isLast,
  });

  return (
    <li className={base()}>
      <div className={marker()}>
        {completed ? <CheckCircle2 className={icon()} /> : <CircleDashed className={icon()} />}
      </div>
      <span className={progressBar()} />
      <div className={content()}>
        {isFirstInGroup && (
          <div className={locationWrap()}>
            <div className={locationLabel()}>{locationName}</div>
            {locationCode && (
              <div className={locationBadgeBlock()}>
                <div className={locationBadge()}>{locationCode}</div>
              </div>
            )}
          </div>
        )}
        <div className={eventType()}>{label}</div>
        {parsedMilestoneDate && (
          <div className={eventDate()}>{formatDistance(parsedMilestoneDate, currentDate, { addSuffix: true })}</div>
        )}
      </div>
      <div className={eventDate()}>{parsedMilestoneDate ? formatDateTimeForUser(parsedMilestoneDate) : ''}</div>
    </li>
  );
};

export const milestoneStyles = tv({
  slots: {
    base: 'relative mb-2 flex items-end justify-between pr-4 leading-5',
    content: 'pl-10 md:pl-28',
    marker: 'absolute -top-0.5 left-2 md:left-20',
    icon: 'z-4 mr-3 size-4 shrink-0 translate-y-1 bg-white text-grey-300',
    locationWrap: 'flex items-center gap-2 md:block',
    locationLabel: 'text-base font-semibold text-grey-800',
    locationBadge:
      'inline-block bg-grey-100 p-1 text-xs font-semibold leading-4 text-grey-800 md:absolute md:right-0 md:top-0 md:block',
    locationBadgeBlock: 'md:absolute md:-top-0.5 md:left-16',
    eventType: 'text-baseSm text-grey-800',
    eventDate: 'whitespace-nowrap text-baseSm text-grey-500',
    progressBar:
      'absolute left-2 top-[18px] ml-[7px] h-full border-l border-dashed border-grey-300 last:-bottom-4 last:h-auto md:left-20',
  },
  variants: {
    isComplete: {
      true: {
        eventText: 'text-grey-700',
        icon: 'text-green-500',
        eventLabel: 'font-medium text-text-primary',
        progressBar: 'border-solid border-green-400',
      },
    },
    isLastInGroup: {
      true: {
        base: 'pb-4',
      },
    },
    isLast: {
      true: {
        progressBar: 'hidden',
      },
    },
  },
});

export { ShipmentMilestoneEvent };
